// useConfirm — drop-in replacement for the native browser window.confirm
// with a styled modal. Mirrors the native API shape (returns a Promise<boolean>)
// so call sites read naturally:
//
//   const ok = await confirm({
//     title: 'Cancel post?',
//     message: 'The source post stays in My Creatives.',
//     destructive: true,
//   });
//   if (!ok) return;
//
// One <ConfirmProvider> at the app root mounts a single modal. Callers
// dispatch via the useConfirm() hook — Provider holds the modal state +
// resolves the Promise on user action.
//
// Why imperative instead of inline JSX modal state per page? Saves ~20
// lines of boilerplate everywhere a confirm is needed, keeps the styled
// modal markup defined in exactly one place, and the same hook can be
// upgraded later (sound, focus trap, etc.) without touching call sites.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface ConfirmOptions {
  /** Bold heading. Short — e.g. "Cancel scheduled post?" */
  title: string;
  /** Subhead body copy. Plain text or React node for emphasised formatting. */
  message: ReactNode;
  /** Button label for the affirmative action. Default: "Confirm" (or
   *  "Delete" when destructive). */
  confirmLabel?: string;
  /** Button label for the negative action. Default: "Cancel". */
  cancelLabel?: string;
  /** When true the confirm button renders red and the icon defaults to a
   *  trash glyph. */
  destructive?: boolean;
  /** Override the header icon. Default depends on `destructive`. */
  icon?: string;
}

interface PendingConfirm {
  opts: ConfirmOptions;
  resolve: (ok: boolean) => void;
}

const ConfirmContext = createContext<(opts: ConfirmOptions) => Promise<boolean>>(
  () => Promise.resolve(false),
);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  // Stable promise-returning entry point given to callers via context.
  // Each invocation stashes the resolve fn so it fires when the user
  // clicks Confirm or Cancel (or dismisses via Escape / backdrop).
  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setPending({ opts, resolve });
    });
  }, []);

  const handleResolve = useCallback(
    (ok: boolean) => {
      if (!pending) return;
      pending.resolve(ok);
      setPending(null);
    },
    [pending],
  );

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <ConfirmModal
          opts={pending.opts}
          onConfirm={() => handleResolve(true)}
          onCancel={() => handleResolve(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  return useContext(ConfirmContext);
}

function ConfirmModal({
  opts,
  onConfirm,
  onCancel,
}: {
  opts: ConfirmOptions;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const destructive = !!opts.destructive;
  const confirmLabel = opts.confirmLabel ?? (destructive ? 'Delete' : 'Confirm');
  const cancelLabel = opts.cancelLabel ?? 'Cancel';
  const icon = opts.icon ?? (destructive ? 'ri-delete-bin-line' : 'ri-question-line');

  // Lock body scroll + handle Escape close. Auto-focus the cancel button
  // on open (safe default — never auto-fires destructive actions).
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') onConfirm();
    };
    window.addEventListener('keydown', onKey);
    // Defer focus to after mount.
    setTimeout(() => cancelRef.current?.focus(), 10);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onCancel, onConfirm]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-label={opts.title}
    >
      <div
        className={`relative w-full max-w-md rounded-2xl glass-stroke bg-white shadow-2xl p-6 ${
          destructive ? 'shadow-red-200/30' : 'shadow-primary/10'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
            destructive ? 'bg-red-50' : 'bg-primary/10'
          }`}
        >
          <i className={`${icon} text-xl ${destructive ? 'text-red-500' : 'text-primary'}`} />
        </div>
        <h3 className="font-display font-bold text-dark text-lg mb-2">
          {opts.title}
        </h3>
        <div className="text-dark-muted text-sm mb-5">
          {opts.message}
        </div>
        <div className="flex gap-3">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 rounded-full border border-surface-dark/40 text-dark text-sm font-semibold hover:bg-surface transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-white text-sm font-bold transition-all cursor-pointer focus:outline-none focus:ring-2 ${
              destructive
                ? 'bg-red-500 hover:bg-red-600 focus:ring-red-300'
                : 'bg-primary hover:bg-primary-hover focus:ring-primary/40'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
